import React, { useState, useEffect } from 'react';
import { Project, Task } from '../types/Project';
import { TaskForm } from './TaskForm';
import { TaskList } from './TaskList';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

interface ProjectDetailProps {
  project: Project;
  tasks: Task[];
  onBack: () => void;
  onCreateTask: (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onUpdateTask: (task: Task) => void;
  onDeleteTask: (id: string) => void;
  onUpdateProject: (project: Project) => void;
  selectedTaskId?: string;
  onClearSelectedTask?: () => void;
}

export const ProjectDetail: React.FC<ProjectDetailProps> = ({
  project,
  tasks,
  onBack,
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
  onUpdateProject,
  selectedTaskId,
  onClearSelectedTask
}) => {
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const projectTasks = tasks.filter(task => task.projectId === project.id);

  // 選択されたタスクがある場合は編集モードを開く
  useEffect(() => {
    if (selectedTaskId) {
      const taskToEdit = projectTasks.find(task => task.id === selectedTaskId);
      if (taskToEdit) {
        setEditingTask(taskToEdit);
        setShowTaskForm(true);
      }
    }
  }, [selectedTaskId, projectTasks]);

  const getStatusColor = (status: Project['status']) => {
    switch (status) {
      case 'planning': return '#fbbf24';
      case 'in-progress': return '#3b82f6';
      case 'completed': return '#10b981';
      case 'on-hold': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status: Project['status']) => {
    switch (status) {
      case 'planning': return '計画中';
      case 'in-progress': return '進行中';
      case 'completed': return '完了';
      case 'on-hold': return '保留';
      default: return '不明';
    }
  };

  const handleCreateTask = (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
    onCreateTask(taskData);
    setShowTaskForm(false);
    // ガントチャートから遷移した場合のselectedTaskIdもクリア
    if (selectedTaskId && onClearSelectedTask) {
      onClearSelectedTask();
    }
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setShowTaskForm(true);
  };

  const handleUpdateTask = (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingTask) {
      const updatedTask: Task = {
        ...editingTask,
        ...taskData,
        updatedAt: new Date()
      };
      onUpdateTask(updatedTask);
      setEditingTask(null);
      setShowTaskForm(false);
      // タスク編集完了後、selectedTaskIdをクリアして画面遷移を可能にする
      if (selectedTaskId && onClearSelectedTask) {
        onClearSelectedTask();
      }
    }
  };

  const handleUpdateProgress = (taskId: string, progress: number) => {
    const task = projectTasks.find(t => t.id === taskId);
    if (task) {
      const updatedTask: Task = {
        ...task,
        progress,
        status: progress === 100 ? 'completed' : progress > 0 ? 'in-progress' : 'not-started',
        updatedAt: new Date()
      };
      onUpdateTask(updatedTask);
    }
  };

  const handleProjectProgressChange = (newProgress: number) => {
    const updatedProject: Project = {
      ...project,
      progress: newProgress,
      status: newProgress === 100 ? 'completed' : newProgress > 0 ? 'in-progress' : 'planning',
      updatedAt: new Date()
    };
    onUpdateProject(updatedProject);
  };

  // タスクの平均進捗を計算
  const calculateAverageProgress = () => {
    if (projectTasks.length === 0) return project.progress;
    const totalProgress = projectTasks.reduce((sum, task) => sum + task.progress, 0);
    return Math.round(totalProgress / projectTasks.length);
  };

  const averageProgress = calculateAverageProgress();

  return (
    <div className="project-detail">
      <div className="project-detail-header">
        <button className="btn-back" onClick={onBack}>
          ← 戻る
        </button>
        <h1>{project.name}</h1>
      </div>

      <div className="project-detail-content">
        <div className="project-info-card">
          <div className="project-info-header">
            <h2>案件情報</h2>
            <div className="project-meta">
              <div 
                className="status-badge" 
                style={{ backgroundColor: getStatusColor(project.status) }}
              >
                {getStatusText(project.status)}
              </div>
            </div>
          </div>

          <div className="project-info-body">
            <div className="info-row">
              <label>説明:</label>
              <p>{project.description || '説明なし'}</p>
            </div>

            <div className="info-row">
              <label>期間:</label>
              <p>
                {format(project.startDate, 'yyyy年MM月dd日', { locale: ja })} 〜 
                {format(project.endDate, 'yyyy年MM月dd日', { locale: ja })}
              </p>
            </div>

            <div className="info-row">
              <label>案件進捗:</label>
              <div className="progress-control">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={project.progress}
                  onChange={(e) => handleProjectProgressChange(parseInt(e.target.value))}
                  className="progress-slider"
                />
                <span className="progress-value">{project.progress}%</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${project.progress}%` }}
                />
              </div>
            </div>

            {projectTasks.length > 0 && (
              <div className="info-row">
                <label>タスク平均進捗:</label>
                <div className="average-progress">
                  <span className="progress-value">{averageProgress}%</span>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ 
                        width: `${averageProgress}%`,
                        backgroundColor: '#10b981'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="tasks-section">
          <div className="tasks-header">
            <h2>タスク管理</h2>
            <button 
              className="btn-primary"
              onClick={() => setShowTaskForm(true)}
            >
              + 新しいタスク
            </button>
          </div>

          <TaskList
            tasks={projectTasks}
            onEditTask={handleEditTask}
            onDeleteTask={onDeleteTask}
            onUpdateProgress={handleUpdateProgress}
          />
        </div>
      </div>

      {showTaskForm && (
        editingTask ? (
          <TaskForm
            projectId={project.id}
            onSubmit={handleUpdateTask}
            onCancel={() => {
              setShowTaskForm(false);
              setEditingTask(null);
              // ガントチャートから遷移した場合のselectedTaskIdもクリア
              if (selectedTaskId && onClearSelectedTask) {
                onClearSelectedTask();
              }
            }}
            existingTasks={projectTasks.filter(t => t.id !== editingTask.id)}
            initialData={editingTask}
          />
        ) : (
          <TaskForm
            projectId={project.id}
            onSubmit={handleCreateTask}
            onCancel={() => {
              setShowTaskForm(false);
              // ガントチャートから遷移した場合のselectedTaskIdもクリア
              if (selectedTaskId && onClearSelectedTask) {
                onClearSelectedTask();
              }
            }}
            existingTasks={projectTasks}
          />
        )
      )}
    </div>
  );
};
